import api, { API_URL } from './api';
import { ApiResponse } from '../types';
import {
  DeploymentCustomer,
  DeploymentHistoryResponse,
  DeploymentOverview,
  DeploymentServerGroupListResponse,
  DeploymentRecord,
  DeploymentReleaseSelection,
  DeploymentServer,
  DeploymentTargetRecord,
  GitLabReleaseOptions,
  ManagedArtifactActionResult,
  ManagedArtifactStatus,
  ParameterReference,
  RepositoryCatalogRecord,
  RolloutMetadataReadinessReport,
  RuntimeConfigInstanceRecord,
  RuntimeConfigTemplateRecord,
  SecretReference,
  SetupRunHistoryResponse,
  SetupServerGroupListResponse,
  ServerLock,
  StackCandidate,
  SetupRecord,
} from '../types/deployment';

type SetupListResponse = {
  setups: SetupRecord[];
  total: number;
  limit: number;
  skip: number;
};

type SetupResponse = {
  setup: SetupRecord;
};

type ServerLocksResponse = {
  locks: ServerLock[];
};

type ServerLockResponse = {
  lock: ServerLock;
};

type StackCandidatesResponse = {
  stacks: StackCandidate[];
};

type StackRunsResponse = {
  stackRuns: SetupRecord[];
  total?: number;
  limit?: number;
  skip?: number;
};

type StackRunResponse = {
  stackRun: SetupRecord;
};

const DRY_RUN_REQUEST_TIMEOUT_MS = 120000;
const HOST_MANAGEMENT_REQUEST_TIMEOUT_MS = 300000;

export const deploymentDashboardApi = {
  getOverview: async (): Promise<ApiResponse<DeploymentOverview>> => {
    const response = await api.get('/deployment-dashboard/overview');
    return response.data;
  },

  getRolloutMetadataReadiness: async (): Promise<ApiResponse<RolloutMetadataReadinessReport>> => {
    const response = await api.get('/deployment-dashboard/rollout-metadata-readiness');
    return response.data;
  },

  getCustomers: async (): Promise<ApiResponse<DeploymentCustomer[]>> => {
    const response = await api.get('/deployment-dashboard/customers');
    return response.data;
  },

  createCustomer: async (payload: Record<string, unknown>): Promise<ApiResponse<DeploymentCustomer>> => {
    const response = await api.post('/deployment-dashboard/customers', payload);
    return response.data;
  },

  updateCustomer: async (id: string, payload: Record<string, unknown>): Promise<ApiResponse<DeploymentCustomer>> => {
    const response = await api.put(`/deployment-dashboard/customers/${id}`, payload);
    return response.data;
  },

  deleteCustomer: async (id: string): Promise<ApiResponse<DeploymentCustomer>> => {
    const response = await api.delete(`/deployment-dashboard/customers/${id}`);
    return response.data;
  },

  getSecretReferences: async (): Promise<ApiResponse<SecretReference[]>> => {
    const response = await api.get('/deployment-dashboard/secret-references');
    return response.data;
  },

  createSecretReference: async (payload: Record<string, unknown>): Promise<ApiResponse<SecretReference>> => {
    const response = await api.post('/deployment-dashboard/secret-references', payload);
    return response.data;
  },

  updateSecretReference: async (id: string, payload: Record<string, unknown>): Promise<ApiResponse<SecretReference>> => {
    const response = await api.put(`/deployment-dashboard/secret-references/${id}`, payload);
    return response.data;
  },

  deleteSecretReference: async (id: string): Promise<ApiResponse<SecretReference>> => {
    const response = await api.delete(`/deployment-dashboard/secret-references/${id}`);
    return response.data;
  },

  getParameterReferences: async (): Promise<ApiResponse<ParameterReference[]>> => {
    const response = await api.get('/deployment-dashboard/parameter-references');
    return response.data;
  },

  createParameterReference: async (payload: Record<string, unknown>): Promise<ApiResponse<ParameterReference>> => {
    const response = await api.post('/deployment-dashboard/parameter-references', payload);
    return response.data;
  },

  updateParameterReference: async (id: string, payload: Record<string, unknown>): Promise<ApiResponse<ParameterReference>> => {
    const response = await api.put(`/deployment-dashboard/parameter-references/${id}`, payload);
    return response.data;
  },

  deleteParameterReference: async (id: string): Promise<ApiResponse<ParameterReference>> => {
    const response = await api.delete(`/deployment-dashboard/parameter-references/${id}`);
    return response.data;
  },

  getServers: async (): Promise<ApiResponse<DeploymentServer[]>> => {
    const response = await api.get('/deployment-dashboard/servers');
    return response.data;
  },

  createServer: async (payload: Record<string, unknown>): Promise<ApiResponse<DeploymentServer>> => {
    const response = await api.post('/deployment-dashboard/servers', payload);
    return response.data;
  },

  updateServer: async (id: string, payload: Record<string, unknown>): Promise<ApiResponse<DeploymentServer>> => {
    const response = await api.put(`/deployment-dashboard/servers/${id}`, payload);
    return response.data;
  },

  deleteServer: async (id: string): Promise<ApiResponse<DeploymentServer>> => {
    const response = await api.delete(`/deployment-dashboard/servers/${id}`);
    return response.data;
  },

  getRuntimeConfigTemplates: async (): Promise<ApiResponse<RuntimeConfigTemplateRecord[]>> => {
    const response = await api.get('/deployment-dashboard/runtime-config-templates');
    return response.data;
  },

  createRuntimeConfigTemplate: async (payload: Record<string, unknown>): Promise<ApiResponse<RuntimeConfigTemplateRecord>> => {
    const response = await api.post('/deployment-dashboard/runtime-config-templates', payload);
    return response.data;
  },

  updateRuntimeConfigTemplate: async (id: string, payload: Record<string, unknown>): Promise<ApiResponse<RuntimeConfigTemplateRecord>> => {
    const response = await api.put(`/deployment-dashboard/runtime-config-templates/${id}`, payload);
    return response.data;
  },

  deleteRuntimeConfigTemplate: async (id: string): Promise<ApiResponse<RuntimeConfigTemplateRecord>> => {
    const response = await api.delete(`/deployment-dashboard/runtime-config-templates/${id}`);
    return response.data;
  },

  getRuntimeConfigInstances: async (): Promise<ApiResponse<RuntimeConfigInstanceRecord[]>> => {
    const response = await api.get('/deployment-dashboard/runtime-config-instances');
    return response.data;
  },

  createRuntimeConfigInstance: async (payload: Record<string, unknown>): Promise<ApiResponse<RuntimeConfigInstanceRecord>> => {
    const response = await api.post('/deployment-dashboard/runtime-config-instances', payload);
    return response.data;
  },

  updateRuntimeConfigInstance: async (id: string, payload: Record<string, unknown>): Promise<ApiResponse<RuntimeConfigInstanceRecord>> => {
    const response = await api.put(`/deployment-dashboard/runtime-config-instances/${id}`, payload);
    return response.data;
  },

  deleteRuntimeConfigInstance: async (id: string): Promise<ApiResponse<RuntimeConfigInstanceRecord>> => {
    const response = await api.delete(`/deployment-dashboard/runtime-config-instances/${id}`);
    return response.data;
  },

  getRepositories: async (): Promise<ApiResponse<RepositoryCatalogRecord[]>> => {
    const response = await api.get('/deployment-dashboard/repositories');
    return response.data;
  },

  syncRepositories: async (): Promise<ApiResponse<RepositoryCatalogRecord[]>> => {
    const response = await api.post('/deployment-dashboard/repositories/sync');
    return response.data;
  },

  createRepository: async (payload: { gitlabProjectId?: number; pathWithNamespace?: string }): Promise<ApiResponse<RepositoryCatalogRecord>> => {
    const response = await api.post('/deployment-dashboard/repositories', payload);
    return response.data;
  },

  updateRepository: async (id: string, payload: Record<string, unknown>): Promise<ApiResponse<RepositoryCatalogRecord>> => {
    const response = await api.put(`/deployment-dashboard/repositories/${id}`, payload);
    return response.data;
  },

  deleteRepository: async (id: string): Promise<ApiResponse<RepositoryCatalogRecord>> => {
    const response = await api.delete(`/deployment-dashboard/repositories/${id}`);
    return response.data;
  },

  getRepositoryReleaseOptions: async (id: string): Promise<ApiResponse<{ repository: RepositoryCatalogRecord; releaseOptions: GitLabReleaseOptions }>> => {
    const response = await api.get(`/deployment-dashboard/repositories/${id}/releases`);
    return response.data;
  },

  getDeploymentTargets: async (): Promise<ApiResponse<DeploymentTargetRecord[]>> => {
    const response = await api.get('/deployment-dashboard/deployment-targets');
    return response.data;
  },

  createDeploymentTarget: async (payload: Record<string, unknown>): Promise<ApiResponse<DeploymentTargetRecord>> => {
    const response = await api.post('/deployment-dashboard/deployment-targets', payload);
    return response.data;
  },

  updateDeploymentTarget: async (id: string, payload: Record<string, unknown>): Promise<ApiResponse<DeploymentTargetRecord>> => {
    const response = await api.put(`/deployment-dashboard/deployment-targets/${id}`, payload);
    return response.data;
  },

  deleteDeploymentTarget: async (id: string): Promise<ApiResponse<DeploymentTargetRecord>> => {
    const response = await api.delete(`/deployment-dashboard/deployment-targets/${id}`);
    return response.data;
  },

  getManagedArtifacts: async (id: string): Promise<ApiResponse<ManagedArtifactStatus[]>> => {
    const response = await api.get(`/deployment-dashboard/deployment-targets/${id}/managed-artifacts`);
    return response.data;
  },

  reconcileManagedArtifacts: async (id: string): Promise<ApiResponse<ManagedArtifactActionResult>> => {
    const response = await api.post(
      `/deployment-dashboard/deployment-targets/${id}/managed-artifacts/reconcile`,
      undefined,
      { timeout: HOST_MANAGEMENT_REQUEST_TIMEOUT_MS }
    );
    return response.data;
  },

  checkTlsCertificate: async (id: string): Promise<ApiResponse<ManagedArtifactActionResult>> => {
    const response = await api.post(
      `/deployment-dashboard/deployment-targets/${id}/tls/check`,
      undefined,
      { timeout: HOST_MANAGEMENT_REQUEST_TIMEOUT_MS }
    );
    return response.data;
  },

  issueTlsCertificate: async (id: string): Promise<ApiResponse<ManagedArtifactActionResult>> => {
    const response = await api.post(
      `/deployment-dashboard/deployment-targets/${id}/tls/issue`,
      undefined,
      { timeout: HOST_MANAGEMENT_REQUEST_TIMEOUT_MS }
    );
    return response.data;
  },

  runTlsRenewalDryRun: async (id: string): Promise<ApiResponse<ManagedArtifactActionResult>> => {
    const response = await api.post(
      `/deployment-dashboard/deployment-targets/${id}/tls/renew-dry-run`,
      undefined,
      { timeout: HOST_MANAGEMENT_REQUEST_TIMEOUT_MS }
    );
    return response.data;
  },

  renewTlsCertificate: async (id: string): Promise<ApiResponse<ManagedArtifactActionResult>> => {
    const response = await api.post(
      `/deployment-dashboard/deployment-targets/${id}/tls/renew`,
      undefined,
      { timeout: HOST_MANAGEMENT_REQUEST_TIMEOUT_MS }
    );
    return response.data;
  },

  getDeployments: async (params?: Record<string, string | number | boolean | undefined>): Promise<ApiResponse<DeploymentRecord[]>> => {
    const response = await api.get('/deployment-dashboard/deployments', { params });
    return response.data;
  },

  getDeploymentServerGroups: async (params?: Record<string, string | number | boolean | undefined>): Promise<DeploymentServerGroupListResponse> => {
    const response = await api.get('/deployment-dashboard/deployments/server-groups', { params });
    return response.data;
  },

  getDeploymentHistory: async (params?: Record<string, string | number | boolean | undefined>): Promise<DeploymentHistoryResponse> => {
    const response = await api.get('/deployment-dashboard/deployments/history', { params });
    return response.data;
  },

  getDeploymentById: async (id: string): Promise<ApiResponse<DeploymentRecord>> => {
    const response = await api.get(`/deployment-dashboard/deployments/${id}`);
    return response.data;
  },

  createDeployment: async (payload: {
    deploymentTargetId: string;
    releaseSelection: DeploymentReleaseSelection;
    runtimeConfigTemplateId?: string | null;
    runtimeConfigInstanceId?: string | null;
    deploymentMode?: string;
    reason?: string;
  }): Promise<ApiResponse<DeploymentRecord>> => {
    const response = await api.post('/deployment-dashboard/deployments', payload, {
      timeout: HOST_MANAGEMENT_REQUEST_TIMEOUT_MS,
    });
    return response.data;
  },

  refreshDeployment: async (id: string): Promise<ApiResponse<DeploymentRecord>> => {
    const response = await api.post(`/deployment-dashboard/deployments/${id}/refresh`, undefined, {
      timeout: HOST_MANAGEMENT_REQUEST_TIMEOUT_MS,
    });
    return response.data;
  },

  restartDeployment: async (id: string, reason?: string): Promise<ApiResponse<DeploymentRecord>> => {
    const response = await api.post(`/deployment-dashboard/deployments/${id}/restart`, { reason }, {
      timeout: HOST_MANAGEMENT_REQUEST_TIMEOUT_MS,
    });
    return response.data;
  },

  composeStartDeployment: async (id: string, reason?: string): Promise<ApiResponse<DeploymentRecord>> => {
    const response = await api.post(`/deployment-dashboard/deployments/${id}/compose-start`, { reason }, {
      timeout: HOST_MANAGEMENT_REQUEST_TIMEOUT_MS,
    });
    return response.data;
  },

  composeStopDeployment: async (id: string, reason?: string): Promise<ApiResponse<DeploymentRecord>> => {
    const response = await api.post(`/deployment-dashboard/deployments/${id}/compose-stop`, { reason }, {
      timeout: HOST_MANAGEMENT_REQUEST_TIMEOUT_MS,
    });
    return response.data;
  },

  rollbackDeployment: async (
    id: string,
    reason: string,
    deploymentMode?: string
  ): Promise<ApiResponse<DeploymentRecord>> => {
    const response = await api.post(`/deployment-dashboard/deployments/${id}/rollback`, { reason, deploymentMode }, {
      timeout: HOST_MANAGEMENT_REQUEST_TIMEOUT_MS,
    });
    return response.data;
  },

  // ── Setup API ──────────────────────────────────────────────────

  listSetups: async (params?: {
    targetId?: string;
    serverId?: string;
    customerId?: string;
    environment?: string;
    status?: string;
    actionType?: string;
    limit?: number;
    skip?: number;
  }): Promise<SetupListResponse> => {
    const response = await api.get('/deployment-dashboard/setups', { params });
    return response.data;
  },

  getSetupServerGroups: async (params?: {
    customerId?: string;
    serverId?: string;
    environment?: string;
    limit?: number;
    skip?: number;
  }): Promise<SetupServerGroupListResponse> => {
    const response = await api.get('/deployment-dashboard/setup-explorer/server-groups', { params });
    return response.data;
  },

  getSetupHistory: async (params: {
    targetId: string;
    status?: string;
    actionType?: string;
    limit?: number;
    skip?: number;
    supersededOnly?: boolean;
  }): Promise<SetupRunHistoryResponse> => {
    const response = await api.get('/deployment-dashboard/setup-explorer/history', { params });
    return response.data;
  },

  getStackHistory: async (params: {
    stackId: string;
    customerId: string;
    serverId: string;
    environment: string;
    status?: string;
    limit?: number;
    skip?: number;
    supersededOnly?: boolean;
  }): Promise<SetupRunHistoryResponse> => {
    const response = await api.get('/deployment-dashboard/setup-explorer/stack-history', { params });
    return response.data;
  },

  getSetup: async (id: string): Promise<SetupResponse> => {
    const response = await api.get(`/deployment-dashboard/setups/${id}`);
    return response.data;
  },

  createSetup: async (payload: {
    deploymentTargetId: string;
    setupMode?: string;
    setupAction?: 'setup' | 'check';
    runtimeEnvContent?: string;
    reason?: string;
    releaseSha?: string;
  }): Promise<SetupResponse> => {
    const response = await api.post('/deployment-dashboard/setups', payload, {
      timeout: HOST_MANAGEMENT_REQUEST_TIMEOUT_MS,
    });
    return response.data;
  },

  createSetupDryRun: async (payload: {
    deploymentTargetId: string;
    setupMode?: string;
    runtimeEnvContent?: string;
    reason?: string;
    releaseSha?: string;
  }): Promise<SetupResponse> => {
    const response = await api.post('/deployment-dashboard/setups/dry-run', payload, {
      timeout: DRY_RUN_REQUEST_TIMEOUT_MS,
    });
    return response.data;
  },

  refreshSetup: async (id: string): Promise<SetupResponse> => {
    const response = await api.post(`/deployment-dashboard/setups/${id}/refresh`, undefined, {
      timeout: HOST_MANAGEMENT_REQUEST_TIMEOUT_MS,
    });
    return response.data;
  },

  createSetupEventStream: (): EventSource => new EventSource(
    `${API_URL}/deployment-dashboard/setups/events/stream`,
    { withCredentials: true }
  ),

  reprovisionCredentials: async (
    targetId: string,
    payload: { setupMode?: string; reason?: string }
  ): Promise<SetupResponse> => {
    const response = await api.post(
      `/deployment-dashboard/deployment-targets/${targetId}/reprovision-credentials`,
      payload,
      { timeout: HOST_MANAGEMENT_REQUEST_TIMEOUT_MS }
    );
    return response.data;
  },

  listAvailableStacks: async (params?: {
    customerId?: string;
    serverId?: string;
    environment?: string;
  }): Promise<StackCandidatesResponse> => {
    const response = await api.get('/deployment-dashboard/stacks', { params });
    return response.data;
  },

  listStackRuns: async (params?: {
    customerId?: string;
    serverId?: string;
    environment?: string;
    status?: string;
    limit?: number;
  }): Promise<StackRunsResponse> => {
    const response = await api.get('/deployment-dashboard/stack-runs', { params });
    return response.data;
  },

  createStackRun: async (payload: {
    stackId: string;
    customerId: string;
    serverId: string;
    environment: string;
    modeOverrides?: Record<string, string>;
    reason?: string;
  }): Promise<StackRunResponse> => {
    const response = await api.post('/deployment-dashboard/stack-runs', payload);
    return response.data;
  },

  createStackDryRun: async (payload: {
    stackId: string;
    customerId: string;
    serverId: string;
    environment: string;
    modeOverrides?: Record<string, string>;
    reason?: string;
  }): Promise<StackRunResponse> => {
    const response = await api.post('/deployment-dashboard/stack-runs/dry-run', payload, {
      timeout: DRY_RUN_REQUEST_TIMEOUT_MS,
    });
    return response.data;
  },

  retryStackRun: async (
    id: string,
    payload?: { reason?: string }
  ): Promise<StackRunResponse> => {
    const response = await api.post(`/deployment-dashboard/stack-runs/${id}/retry`, payload || {});
    return response.data;
  },

  // ── Server locks API ───────────────────────────────────────────

  listServerLocks: async (params?: {
    serverId?: string;
    targetId?: string;
    activeOnly?: boolean;
  }): Promise<ServerLocksResponse> => {
    const response = await api.get('/deployment-dashboard/server-locks', { params });
    return response.data;
  },

  getServerLock: async (id: string): Promise<ServerLockResponse> => {
    const response = await api.get(`/deployment-dashboard/server-locks/${id}`);
    return response.data;
  },

  forceReleaseLock: async (
    id: string,
    reason: string
  ): Promise<ServerLockResponse> => {
    const response = await api.post(`/deployment-dashboard/server-locks/${id}/force-release`, { reason });
    return response.data;
  },
};
